// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the AYNI chatbot's statistics (owner, 2026-10-06, choice E): the
 * agent commands `luma.stats`, `luma.extremum`, `luma.zonal` and
 * `luma.transect` answered from the frame Analyze measures, with the same
 * functions (`datasetStats.ts`), so a number the chatbot says is a number
 * the panel shows. A region's statistics and its latitude profile also open
 * Analyze on that region, so the visitor sees what was computed.
 *
 * Never a guess: without values, outside the plan, or outside the data the
 * answer is `{ available: false, reason }`, which the engine passes to the
 * model as it is. Values are rounded to the data's own step.
 *
 * AYNI (2026-10-07, AYNI 3D's research and education contract): the
 * Research environment's commands as well (`createChatResearch`): a
 * baseline held or read over a period, the change on the globe, Compare,
 * the area over a threshold, the Line over time, the methods paragraph,
 * the figure and the bundle, each driven through Analyze's own controls
 * and read back from what Analyze computed; and `explainReading` for
 * `explain.value`.
 */
import {
  areaAboveKm2,
  buildHistogram,
  findExtremum,
  sampleTransect,
  summarize,
  summarizeTransect,
  transectSampleCount,
  windowForBounds,
  zonalMeans,
  type LatLonBounds,
  type TexelWindow,
} from '../services/datasetStats'
import { dataToolsAllowed, regionStatsLocked } from '../services/planAccess'
import {
  ANALYZE_ZONES,
  analysisFrameNow,
  analyzeLineSet,
  analyzeScope,
  chatBaselineState,
  clearBaselineNow,
  compareChoices,
  compareNow,
  focusBundleButton,
  isAnalyzeUIOpen,
  markPeriodByAssistant,
  openAnalyzeScoped,
  openAnalyzeUI,
  periodRefusalNow,
  prepareBaseline,
  pressInAnalyze,
  quantStep,
  setAnalyzeLine,
  setAnalyzeTab,
  setChangeModeNow,
  setCompareChoice,
  stepPeriodNow,
  type AnalyzeScope,
  type AnalyzeTab,
  type AnalyzeZoneId,
} from './analyzeUI'
import { hovmollerSummary } from './hovmollerUI'
import { setThresholdRule, thresholdMeasured } from './thresholdUI'
import { changeShown, changeViewState, viewReading } from '../services/research/changeView'
import { buildMethods, type MethodsInput } from '../services/research/methods'
import type { ProbeReading } from '../services/datasetProbe'
import type { ColorScaleDisplay } from '../services/colorScaleDisplay'
import { releaseOf } from '../types/color-scale'
import type { AgentToolOutcome } from '../ayni3d/shared/protocol'
import type { XrChatResearch } from '../ayni-chat/xrChatExecutor'
import {
  CHANGE_REFUSAL_TEXT,
  COMPARE_REFUSAL_TEXT,
  PERIOD_REFUSAL_TEXT,
  baselineOutcome,
  changeOutcome,
  compareOutcome,
  explainOutcome,
  hovmollerOutcome,
  methodsOutcome,
  ok,
  pending,
  thresholdOutcome,
  unavailable,
  type ChatExplain,
} from '../ayni-chat/xrChatOutcomes'
import { t } from '../i18n'
import { formatNumber } from '../i18n/format'

export type ChatAnalysisRequest =
  | { kind: 'stats'; bounds?: LatLonBounds; threshold?: number }
  | { kind: 'extremum'; which: 'max' | 'min'; bounds?: LatLonBounds }
  | { kind: 'zonal'; bounds?: LatLonBounds; maxRows?: number }
  | { kind: 'transect'; from: { lat: number; lon: number }; to: { lat: number; lon: number }; samples?: number }

export type ChatAnalysis = { available: false; reason: string } | ({ available: true } & Record<string, unknown>)

/** At most this many latitude rows go back to the model. */
const MAX_ZONAL_ROWS = 36
/** At most this many points along a line go back to the model. */
const MAX_TRANSECT_POINTS = 40

const no = (reason: string): ChatAnalysis => ({ available: false, reason })

/** A value to the data's own step, else four significant figures. */
export function roundToStep(value: number, step: number | null): number {
  if (!Number.isFinite(value)) return value
  if (step && step > 0) {
    const decimals = Math.max(0, Math.min(6, Math.ceil(-Math.log10(step))))
    return Number(value.toFixed(decimals))
  }
  return Number(value.toPrecision(4))
}

function describeBounds(b: LatLonBounds): string {
  const lat = (v: number) => `${Math.abs(v)}°${v >= 0 ? 'N' : 'S'}`
  const lon = (v: number) => `${Math.abs(v)}°${v >= 0 ? 'E' : 'W'}`
  return `${lat(b.s)} to ${lat(b.n)}, ${lon(b.w)} to ${lon(b.e)}` // i18n-exempt: for the model, which answers in the visitor's language
}

/** A named band when the box is one (the Arctic, the tropics…), else the box, labelled as the chatbot's. */
export function scopeFor(bounds: LatLonBounds | undefined): AnalyzeScope {
  if (!bounds) return { kind: 'dataset' }
  const near = (a: number, b: number) => Math.abs(a - b) <= 0.5
  for (const [id, z] of Object.entries(ANALYZE_ZONES) as Array<[AnalyzeZoneId, LatLonBounds]>) {
    if (near(z.n, bounds.n) && near(z.s, bounds.s) && near(z.w, bounds.w) && near(z.e, bounds.e)) return { kind: 'zone', id }
  }
  const deg = (v: number) => `${formatNumber(v, { maximumFractionDigits: 1 })}°`
  return { kind: 'box', bounds, label: t('analyze.scope.chatBox', { s: deg(bounds.s), n: deg(bounds.n), w: deg(bounds.w), e: deg(bounds.e) }) }
}

/**
 * Run one request. `show`: open Analyze on the region measured (not in a
 * meeting's audience, where the presenter decides what is on screen).
 */
export function runChatAnalysis(request: ChatAnalysisRequest, show: boolean): ChatAnalysis {
  if (!dataToolsAllowed()) return no('Measuring the data needs a plan with the data tools.')
  const now = analysisFrameNow()
  if (!now) return no('The dataset on screen carries no values to measure (no dataset, or a picture).')
  const { snapshot, scale, options } = now.frame
  const step = quantStep(scale)
  const r = (v: number) => roundToStep(v, step)
  const units = scale.units ?? null
  const frame = now.frameTime

  if (request.kind === 'transect') {
    const samples = Math.max(2, Math.min(request.samples ?? transectSampleCount(snapshot, request.from, request.to, options), 512))
    const points = sampleTransect(snapshot, scale, request.from, request.to, samples, options)
    const summary = summarizeTransect(points)
    if (!summary) return no('The line crosses no data on this frame.')
    const every = Math.max(1, Math.ceil(points.length / MAX_TRANSECT_POINTS))
    return {
      available: true,
      frame, units,
      lengthKm: Math.round(summary.lengthKm),
      samples: summary.samples,
      withData: summary.withData,
      min: r(summary.min), max: r(summary.max),
      mean: r(summary.mean),
      meanNote: 'unweighted mean of evenly spaced points along the line',
      profile: points.filter((_, i) => i % every === 0).map((p) => ({ km: Math.round(p.distanceKm), value: p.value === null ? null : r(p.value) })),
    }
  }

  // A region: the box asked for, or the whole frame (a plan with whole-region statistics).
  if (!request.bounds && regionStatsLocked()) {
    return no('Statistics of the whole globe or a named region come with the Research and Education plans. On this plan, give a box (north, south, west, east) to measure.')
  }
  let window: TexelWindow | undefined
  if (request.bounds) {
    const w = windowForBounds(snapshot, request.bounds, options)
    if (!w) return no('That box lies outside the dataset.')
    window = w
  }
  const region = request.bounds ? describeBounds(request.bounds) : 'the whole frame'
  const scope = scopeFor(request.bounds)

  if (request.kind === 'extremum') {
    const found = findExtremum(snapshot, scale, request.which, options, window)
    if (!found) return no('No values in that region on this frame.')
    return {
      available: true,
      frame, units, region,
      which: request.which === 'max' ? 'highest' : 'lowest',
      value: r(found.value),
      lat: Math.round(found.lat * 100) / 100,
      lon: Math.round(found.lon * 100) / 100,
      sharedBy: found.tieCount,
      ...(found.tieCount > 1 ? { note: `${found.tieCount} grid cells share this value; this is one of them.` } : {}),
    }
  }

  const stats = summarize(snapshot, scale, options, window)
  if (!stats) return no('No values in that region on this frame.')
  if (show) {
    setAnalyzeTab(request.kind === 'zonal' ? 'profiles' : 'stats')
    openAnalyzeScoped(scope)
  }

  if (request.kind === 'zonal') {
    const rows = zonalMeans(snapshot, scale, options, window).filter((row) => row.mean !== null)
    const cap = Math.max(2, Math.min(request.maxRows ?? MAX_ZONAL_ROWS, MAX_ZONAL_ROWS))
    const every = Math.max(1, Math.ceil(rows.length / cap))
    return {
      available: true,
      frame, units, region,
      shownInAnalyze: show,
      rows: rows.filter((_, i) => i % every === 0).map((row) => ({ lat: Math.round(row.lat * 10) / 10, mean: r(row.mean!) })),
      note: 'Area-weighted mean across longitude at each latitude.',
    }
  }

  const result: ChatAnalysis = {
    available: true,
    frame, units, region,
    shownInAnalyze: show,
    mean: r(stats.mean), median: r(stats.median),
    min: r(stats.min), max: r(stats.max),
    p10: r(stats.p10), p90: r(stats.p90),
    stdDev: r(stats.stdDev),
    coveragePercent: Math.round(stats.coverage * 1000) / 10,
    areaKm2: Math.round(stats.areaKm2),
    resolution: step,
    note: 'Area-weighted over the cells with data.',
  }
  if (request.threshold !== undefined) {
    const hist = buildHistogram(snapshot, scale, options, window)
    result.threshold = request.threshold
    result.areaAboveKm2 = Math.round(areaAboveKm2(hist, scale, request.threshold))
  }
  return result
}

// --- AYNI: the Research environment for the chatbot (AYNI 3D's research and education contract, 2026-10-07) ---

/** What the research tools need from the page beyond Analyze (`main.ts`). */
export interface ChatResearchDeps {
  /** The plan includes the research tools (`researchToolsAllowed`). */
  allowed(): boolean
  /** The Data tab's pins as shown, with its emergence settings (`DataPinsHandle.statsView`). */
  stats(): ReturnType<XrChatResearch['stats']>
  /** The methods paragraph's input for the session now (main.ts `methodsInput`); null without a dataset. */
  methodsInput(): MethodsInput | null
  /** Open the figure dialog on a kind of map; false without a dataset. */
  openFigure(kind: 'globe' | 'equal-earth' | undefined): boolean
}

/** Open Analyze on a region (the one it has when none is given), whatever the plan's region rule: the executor has checked the plan. */
function openOn(bounds: LatLonBounds | undefined, tab: AnalyzeTab): void {
  setAnalyzeTab(tab)
  openAnalyzeUI(null, bounds ? scopeFor(bounds) : analyzeScope())
}

const REGION_BOX_TEXT = 'Here the statistics are measured over a drawn box: give a box (north, south, west, east), or draw one with Box.'

/**
 * The research tools as the chatbot drives them: each sets what its
 * control would set, opens Analyze where the result is shown, and reports
 * what the panel computed, through `xrChatOutcomes.ts`.
 */
export function createChatResearch(deps: ChatResearchDeps): XrChatResearch {
  const boxOnly = (bounds?: LatLonBounds) => !bounds && regionStatsLocked() && analyzeScope().kind !== 'box'
  return {
    allowed: () => deps.allowed(),
    stats: () => deps.stats(),

    baselineHold(request) {
      if (!analysisFrameNow()) return unavailable('no-values', PERIOD_REFUSAL_TEXT['no-values'])
      if ('frame' in request) {
        if (boxOnly()) return unavailable('region', REGION_BOX_TEXT)
        prepareBaseline('frame')
        openOn(undefined, 'baseline')
        if (!pressInAnalyze('.analyze-baseline-hold')) return unavailable('no-values', PERIOD_REFUSAL_TEXT['no-values'])
        return baselineOutcome(chatBaselineState())
      }
      const period = request.period === 'default' ? null : { from: request.period.from, to: request.period.to }
      const now = chatBaselineState()
      // The same period already held or being read: say how far it is, rather than start again.
      if (now.held && now.kind === 'period' && (period === null ? now.isDefault : now.from === period.from && now.to === period.to)) {
        openOn(undefined, 'baseline')
        if (!now.complete) stepPeriodNow()
        return baselineOutcome(chatBaselineState())
      }
      const refusal = periodRefusalNow(period)
      if (refusal) return unavailable(refusal, PERIOD_REFUSAL_TEXT[refusal])
      if (now.held && now.kind === 'period') clearBaselineNow()
      prepareBaseline('period', period)
      openOn(undefined, 'baseline')
      if (!pressInAnalyze('.analyze-period-start')) return unavailable('no-values', PERIOD_REFUSAL_TEXT['no-values'])
      markPeriodByAssistant()
      // Read what playback has not passed yet, one frame after another (playback pauses).
      stepPeriodNow()
      return baselineOutcome(chatBaselineState())
    },

    baselineStatus() {
      return baselineOutcome(chatBaselineState())
    },

    baselineClear() {
      clearBaselineNow()
      return baselineOutcome({ held: false })
    },

    changeShow(mode) {
      const result = setChangeModeNow(mode === 'off' ? null : mode)
      if (!result.ok) return unavailable(result.reason, CHANGE_REFUSAL_TEXT[result.reason])
      const held = chatBaselineState()
      prepareBaseline(held.held && held.kind === 'period' ? 'period' : 'frame')
      openOn(undefined, 'baseline')
      return changeOutcome(mode, result.units)
    },

    compare(command) {
      if (!analysisFrameNow()) return unavailable('no-values', COMPARE_REFUSAL_TEXT['no-values'])
      const choices = compareChoices().filter((c) => c.kind === command.source)
      if (!choices.length) return unavailable('no-candidates', COMPARE_REFUSAL_TEXT['no-candidates'])
      if (command.id && !choices.some((c) => c.id === command.id)) {
        return unavailable('not-found', `${COMPARE_REFUSAL_TEXT['not-found']} Choices: ${choices.map((c) => `${c.id} (${c.title})`).join('; ')}.`)
      }
      setCompareChoice({ source: command.source, id: command.id, pairing: command.pairing, ...(command.mode ? { mode: command.mode } : {}) })
      openOn(command.bounds, 'compare')
      const found = compareNow()
      if (!found) return unavailable('no-values', COMPARE_REFUSAL_TEXT['no-values'])
      if (!found.ok) {
        if (found.reason === 'waiting') return pending(0, 1, 'frames', 'waiting for the other frame to arrive; ask again in a moment')
        return unavailable(found.reason, COMPARE_REFUSAL_TEXT[found.reason] ?? found.reason)
      }
      return compareOutcome(found.result)
    },

    threshold(command) {
      if (!analysisFrameNow()) return unavailable('no-values', 'The dataset on screen carries no values to measure.')
      if (boxOnly(command.bounds)) return unavailable('region', REGION_BOX_TEXT)
      const field = changeViewState().field
      if (command.mode !== 'value' && !field) return unavailable('no-baseline', CHANGE_REFUSAL_TEXT['no-baseline'])
      if (command.mode === 'sigma' && !field?.period?.clim.hasSigma) return unavailable('sigma-needs-period', CHANGE_REFUSAL_TEXT['sigma-needs-period'])
      const rule = { mode: command.mode, comparator: command.comparator, threshold: command.threshold }
      setThresholdRule(rule)
      openOn(command.bounds, 'stats')
      const m = thresholdMeasured()
      if (!m) return unavailable('no-values', 'Nothing could be measured there (no values in the region, or a dataset made of classes).')
      // The section measures values when the held baseline does not apply to this frame (another grid or units).
      if (m.rule.mode !== command.mode) return unavailable('no-baseline', 'The held baseline does not apply to the dataset on screen (another grid or other units).')
      return thresholdOutcome(m.result, { rule: m.rule, ruleText: m.ruleText, units: m.units, frame: m.frame, region: m.region, sigmaFloored: m.sigmaFloored })
    },

    hovmoller(command) {
      if (command.from && command.to) {
        if (!setAnalyzeLine(command.from, command.to)) return unavailable('no-line', 'There is no globe to draw a Line on here.')
      } else if (!analyzeLineSet()) {
        return unavailable('no-line', 'No Line is drawn: give from and to, or draw a Line in Analyze (Profiles).')
      }
      const summary = hovmollerSummary()
      if (!summary) return unavailable('no-line', 'The Line over time is not recording (no values over time on this dataset, or the panel is closed).')
      return hovmollerOutcome(summary)
    },

    methods() {
      const input = deps.methodsInput()
      if (!input) return unavailable('no-dataset', 'There is no dataset on screen to describe.')
      return methodsOutcome(buildMethods(input))
    },

    figure(kind) {
      if (!deps.openFigure(kind)) return unavailable('no-dataset', 'There is no dataset on screen to make a figure of.')
      return ok({ opened: true, kind: kind ?? 'globe' }, 'The figure dialog is open on the screen.', [{ code: 'userExports', text: 'The user chooses the settings and exports the figure; nothing is saved by the chatbot.' }])
    },

    bundle() {
      setAnalyzeTab('stats')
      if (!isAnalyzeUIOpen()) openAnalyzeUI(null, analyzeScope())
      if (!focusBundleButton()) return unavailable('unavailable', 'The Bundle button is not available here.')
      return ok({ shown: true }, 'Analyze is open and its Bundle (.zip) button has the focus.', [{ code: 'userPresses', text: 'The user presses Bundle to save the zip; the chatbot never does.' }])
    },
  }
}

/** What the explanation needs of the page: the raw value at a place, and the display settings. */
export interface ChatExplainDeps {
  probe(lat: number, lon: number): ProbeReading | null
  display(): ColorScaleDisplay
}

/**
 * `explain.value`: what a reading means on the dataset on screen: its
 * units, range, smallest step, palette, what a gap means, its classes; and
 * at a place, the value there, and the change when a change is shown.
 */
export function explainReading(deps: ChatExplainDeps, point: { lat: number; lon: number } | null): AgentToolOutcome {
  const now = analysisFrameNow()
  if (!now) return unavailable('no-values', 'The dataset on screen carries no values (no dataset, or a picture): its colours are only a picture.')
  const { scale } = now.frame
  const release = releaseOf(scale)
  const kind: ChatExplain['scale'] = release?.kind === 'luma8-classified' ? 'classified' : release?.kind === 'luma8-log' ? 'log' : 'linear'
  const step = quantStep(scale)
  const display = deps.display()
  const explain: ChatExplain = {
    units: scale.units ?? null,
    range: { min: scale.vmin, max: scale.vmax },
    step,
    palette: `${display.palette}${display.inverted ? ' (inverted)' : ''}`,
    noData: 'A place with no colour has no data on this frame: outside the dataset\'s coverage, or nothing measured there; it is never zero.',
    classes: release?.kind === 'luma8-classified' ? release.classes.map((c) => c.label) : [],
    scale: kind,
    frame: now.frameTime,
  }
  if (point) {
    const raw = deps.probe(point.lat, point.lon)
    const view = changeViewState()
    const shown = raw && changeShown(view) ? viewReading(raw, point.lat, point.lon, view) : null
    const r = (v: number) => roundToStep(v, step)
    explain.point = {
      lat: point.lat,
      lon: point.lon,
      value: raw && !raw.noData ? r(raw.value) : null,
      noData: !raw || raw.noData,
      ...(shown
        ? { change: shown.noData ? null : Number(shown.value.toPrecision(4)), changeMode: view.other ? 'compare' : view.mode, changeUnits: shown.units ?? null }
        : {}),
    }
  }
  return explainOutcome(explain)
}
